import { useNavigate } from 'react-router-dom';

const AsiaMarketSection: React.FC = () => {
  const navigate = useNavigate();

  const handleQuoteClick = () => {
    navigate('/portal?modal=consultation');
  };

  const markets = [
    {
      country: 'Vietnam',
      capital: 'Ho Chi Minh City',
      population: '97.3M',
      gdpGrowth: '+6.8%',
      description: 'Rapidly growing consumer market with increasing urbanization and digital adoption',
      image: 'https://public.readdy.ai/ai/img_res/6e7a1591421aa73b9a999afd6c9e44da.jpg'
    },
    {
      country: 'Cambodia',
      capital: 'Phnom Penh',
      population: '16.7M',
      gdpGrowth: '+5.2%',
      description: 'Emerging market with strong infrastructure development and growing middle class',
      image: 'https://readdy.ai/api/search-image?query=Phnom%20Penh%20Cambodia%20modern%20city%20center%20with%20outdoor%20advertising%20infrastructure%2C%20contemporary%20billboards%20and%20digital%20displays%20in%20commercial%20district%2C%20developing%20urban%20landscape%20with%20premium%20advertising%20locations%2C%20professional%20commercial%20photography%20with%20clear%20daylight&width=400&height=300&seq=cambodia-urban-ads-2024&orientation=landscape'
    },
    {
      country: 'Singapore',
      capital: 'Singapore',
      population: '5.9M',
      gdpGrowth: '+3.1%',
      description: 'Premium market hub with high purchasing power and international business presence',
      image: 'https://readdy.ai/api/search-image?query=Singapore%20Marina%20Bay%20financial%20district%20with%20premium%20digital%20advertising%20displays%20and%20LED%20billboards%2C%20luxury%20commercial%20real%20estate%20with%20sophisticated%20outdoor%20advertising%2C%20modern%20Southeast%20Asian%20business%20hub%20with%20high-end%20retail%20advertising%2C%20professional%20architectural%20photography%20with%20evening%20lighting&width=400&height=300&seq=singapore-premium-ads-2024&orientation=landscape'
    },
    {
      country: 'Japan',
      capital: 'Tokyo',
      population: '125.8M',
      gdpGrowth: '+1.8%',
      description: 'Mature market with sophisticated consumers and advanced advertising technology',
      image: 'https://readdy.ai/api/search-image?query=Tokyo%20Shibuya%20crossing%20with%20massive%20LED%20digital%20billboards%20and%20electronic%20advertising%20displays%2C%20iconic%20Japanese%20urban%20intersection%20with%20bright%20neon%20signs%20and%20commercial%20advertising%2C%20bustling%20pedestrian%20area%20with%20sophisticated%20outdoor%20advertising%20technology%2C%20professional%20night%20photography%20with%20vibrant%20city%20lights&width=400&height=300&seq=tokyo-shibuya-ads-2024&orientation=landscape'
    }
  ];

  const features = [
    {
      icon: 'ri-global-line',
      title: 'Cross-Border Expertise',
      description: 'Seamless campaign execution across multiple Asian markets with local compliance'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Premium Locations',
      description: 'Exclusive access to high-traffic areas in major cities and transportation hubs'
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Performance Analytics',
      description: 'Real-time campaign monitoring and detailed ROI measurement across all markets'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Regulatory Compliance',
      description: 'Full compliance with local advertising regulations and permit requirements'
    }
  ];

  return (
    <section id="asia-market-section" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-3 sm:mb-4 lg:mb-6">
            Southeast Asia & Japan Premium Advertising Network
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 font-light max-w-4xl mx-auto">
            Expand your brand presence across Asia's most dynamic markets with our comprehensive outdoor advertising solutions
          </p>
        </div>

        {/* Markets Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16 lg:mb-20">
          {markets.map((market, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="relative">
                <img
                  src={market.image}
                  alt={`${market.country} advertising market`}
                  className="w-full h-32 sm:h-40 lg:h-48 object-cover object-top rounded-t-xl"
                />
                <div className="absolute top-2 sm:top-3 lg:top-4 left-2 sm:left-3 lg:left-4">
                  <span className="bg-blue-600 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                    {market.country}
                  </span>
                </div>
              </div>
              
              <div className="p-3 sm:p-4 lg:p-6">
                <h3 className="text-base sm:text-lg lg:text-xl font-medium text-gray-900 mb-2">
                  {market.capital}
                </h3>
                <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 lg:mb-4">
                  <span>Pop: {market.population}</span>
                  <span className="text-green-600 font-medium">{market.gdpGrowth}</span>
                </div>
                <p className="text-gray-600 font-light text-xs sm:text-sm leading-relaxed">
                  {market.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Features Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 xl:p-12 mb-8 sm:mb-12 lg:mb-16">
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-light text-gray-900 mb-4 sm:mb-6 lg:mb-8 text-center">
            Why Choose Our Asia Network
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 lg:mb-4">
                  <i className={`${feature.icon} text-white text-lg sm:text-xl lg:text-2xl`}></i>
                </div>
                <h4 className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 mb-1 sm:mb-2 lg:mb-3">
                  {feature.title}
                </h4>
                <p className="text-gray-600 font-light text-xs sm:text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Success Metrics */}
        <div className="bg-white rounded-xl sm:rounded-2xl border p-4 sm:p-6 lg:p-8 mb-8 sm:mb-12 lg:mb-16">
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-light text-gray-900 mb-4 sm:mb-6 lg:mb-8 text-center">
            Proven Results Across Asia
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-light text-blue-600 mb-1 sm:mb-2">85%+</div>
              <div className="text-gray-600 font-light text-xs sm:text-sm lg:text-base">Campaign Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-light text-blue-600 mb-1 sm:mb-2">12+</div>
              <div className="text-gray-600 font-light text-xs sm:text-sm lg:text-base">Active Markets</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-light text-blue-600 mb-1 sm:mb-2">500M+</div>
              <div className="text-gray-600 font-light text-xs sm:text-sm lg:text-base">Monthly Impressions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-light text-blue-600 mb-1 sm:mb-2">24/7</div>
              <div className="text-gray-600 font-light text-xs sm:text-sm lg:text-base">Campaign Monitoring</div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-light text-gray-900 mb-3 sm:mb-4 lg:mb-6">
            Ready to Expand Across Asia?
          </h3>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 font-light mb-4 sm:mb-6 lg:mb-8 max-w-3xl mx-auto">
            Get a customized proposal for your Asian market expansion with detailed location recommendations and budget optimization
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button 
              onClick={handleQuoteClick}
              className="bg-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base lg:text-lg whitespace-nowrap cursor-pointer"
            >
              Request Asia Market Quote
            </button>
            <button 
              onClick={handleQuoteClick}
              className="border border-blue-600 text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-blue-50 transition-colors font-medium text-sm sm:text-base lg:text-lg whitespace-nowrap cursor-pointer"
            >
              Schedule Free Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AsiaMarketSection;
